import { SB } from '@play-co/replicant';
import createScheduledActions, {
  extractScheduledActionHandlers,
} from '../config/scheduledActions/createScheduledActions';
import { getChatbotFlexMessage } from '../chatbot/flex/utils';
import { getLocale } from '../getters/user';
import {
  getAnalyticsAttributeUserProperties,
  getAnalyticsUserProperties,
  getEquippedPetSpecies,
  getPetName,
} from '../pet/pet.getters';
import { generateReplicantChatbotPayload } from '../chatbot/messageTemplates';
import { deliverChatbotMessage } from '../chatbot/chatbot.util';
import { fatePermanentStatusEffects } from '../fate/fate.ruleset';
import * as Fate from '../fate';
import * as Pet from '../pet';
import { scheduleStatusUpdateMessage } from '../petChatbot/petChatbot.scheduledActions';
import {
  ResurrectionCreativeVariant,
  resurrectionVariants,
} from '../chatbot/flex/resurrect';
import { rescueLevelActivateFromOA } from '../rescueLevel/rescueLevel.modifiers';
import { setResurrectionReward } from '../resurrection/resurrection.modifiers';

const schema = {
  personalizedResurrectionOA: SB.object({
    creativeID: resurrectionVariants,
    subFeature: SB.string().default('OA'),
  }),
  restorePet: SB.object({}),
};

const actions = createScheduledActions(schema)({
  /**
   * Send a personalized resurrection message over CS tool tasksV2 scheduleActionToMany
   */
  personalizedResurrectionOA: async (
    state,
    args: {
      creativeID: ResurrectionCreativeVariant;
      subFeature: string;
    },
    api,
  ) => {
    const feature = 'resurrect';
    const subFeature = args.subFeature ?? 'OA';
    const creativeAssetID = args.creativeID;
    const now = api.date.now();

    const payload = generateReplicantChatbotPayload({
      feature,
      $subFeature: subFeature,
      $creativeAssetID: creativeAssetID,
      templateID: 'resurrect',
      $creativeCTA: creativeAssetID,
      $creativeTextID: creativeAssetID,
      contextID: 'none',
    });

    const message = getChatbotFlexMessage({
      template: 'resurrect',
      payload,
      data: {
        creativeAssetID,
        locale: getLocale(state),
        species: getEquippedPetSpecies(state) ?? 'ball',
        petName: getPetName(state) ?? 'Buruburu',
      },
      analyticsUserProperties: {
        ...getAnalyticsUserProperties(state, now),
        ...getAnalyticsAttributeUserProperties(state, now),
      },
    });

    if (creativeAssetID === '2025-11-19-hundred-gems') {
      setResurrectionReward(state, 'hundredGems', now);
    }

    if (creativeAssetID === '2025-11-19-revive') {
      setResurrectionReward(state, 'revivePet', now);
    }

    if (
      creativeAssetID === '2025-12-12-migration_tos_cryingburu' ||
      creativeAssetID === '2025-12-12-migration_tos_cryinggems' ||
      creativeAssetID === '2025-12-12-migration_tos_pleadingburu' ||
      creativeAssetID === '2025-12-12-migration_tos_pleadinggems'
    ) {
      setResurrectionReward(state, 'migrationGems', now);
    }

    if (creativeAssetID === '2026-04-28-50_gems_bear') {
      setResurrectionReward(state, 'fiftyGemsBear', now);
    }

    if (creativeAssetID === '2026-04-28-50_gems_bear_capy') {
      setResurrectionReward(state, 'fiftyGemsBearCapy', now);
    }

    const delivered = deliverChatbotMessage(state, api, {
      receiverId: state.id,
      message,
      channel: 'oa',
    });

    if (delivered) rescueLevelActivateFromOA(state, creativeAssetID);
  },

  /**
   * Make pet happy and healthy again
   */
  restorePet: async (state, _, api) => {
    const now = api.date.now();
    fatePermanentStatusEffects.forEach((effect) => {
      Fate.modifiers.resolveStatusEffect(state, effect, false, now);
    });

    // Reset pet stats.
    const { setAttribute } = Pet.modifiers;
    setAttribute(state, 'stomach', 100, now);
    setAttribute(state, 'love', 100, now);
    setAttribute(state, 'dirtiness', 0, now);
    setAttribute(state, 'toothAcidity', 0, now);
    setAttribute(state, 'urine', 0, now);

    const currentSpecies = Pet.getters.getEquippedPetSpecies(state);

    if (currentSpecies) {
      scheduleStatusUpdateMessage(
        {
          nextPetState: Fate.getters.getNextPetState(state, now),
          currentSpecies,
          now,
        },
        api,
      );
    }
  },
});

export default {
  schema,
  actions: extractScheduledActionHandlers(actions),
};
